/**
 * 権限管理の管理画面 (ユーザー招待 / 権限グループの作成・編集 / 権限付与) で共用するモーダルの殻 (権限管理 SBI-8)。
 *
 * 任意テキストテンプレートのモーダル (`FreeTextTemplateFormModal`) と同じ骨格
 * (`.modal-backdrop` > `form.modal` > `.modal-header` / `.modal-body` / `.modal-footer`)。
 *
 * - 背景クリック・ ESC では閉じない (誤操作で入力を失わないため)。閉じるのはキャンセル / × のみ。
 * - エラーバナー (原因欄が特定できないエラー) は本文の先頭に出す。欄ごとのエラーは {@link FieldError} を使う。
 * - 確認ダイアログ (`useConfirmDialog().confirmDialog`) は `overlay` に渡す。
 */
import { useEffect, useRef } from 'react';
import type React from 'react';

import { Spinner } from './Spinner';
import { StatusBanner } from './StatusBanner';

/** 入力欄の直下に出す、原因欄のエラー文 (赤枠 `.is-invalid` と対)。 */
export function FieldError(props: {
  id: string;
  message: string | undefined;
}): React.JSX.Element | null {
  if (!props.message) return null;
  return (
    <span id={props.id} className="field-error" data-testid={props.id}>
      {props.message}
    </span>
  );
}

export interface AdminFormModalProps {
  /** `data-testid` の接頭辞 (例: `permission-group`)。`-modal` `-form` `-save` `-cancel-edit` を付ける */
  testIdPrefix: string;
  title: string;
  /** 保存ボタンのラベル (例: 「登録」「更新」「招待する」) */
  saveLabel: string;
  /** 保存中 (二重送信を防ぐ) */
  saving: boolean;
  /** 原因欄が特定できないエラー (null = 非表示) */
  errorMessage: string | null;
  onDismissError: () => void;
  /** 保存ボタン / Enter で呼ばれる */
  onSubmit: () => void;
  /** 閉じる (キャンセル / ×)。破棄確認は呼び出し側の責務 */
  onClose: () => void;
  /** 保存ボタンを無効にしたいとき (例: 選択肢を読込中) */
  saveDisabled?: boolean;
  maxWidth?: number;
  children: React.ReactNode;
  /** モーダルの内側で描画するダイアログ (確認ダイアログなど) */
  overlay?: React.ReactNode;
}

export function AdminFormModal(props: AdminFormModalProps): React.JSX.Element {
  const {
    testIdPrefix,
    title,
    saveLabel,
    saving,
    errorMessage,
    onDismissError,
    onSubmit,
    onClose,
    saveDisabled = false,
    maxWidth = 640,
    children,
    overlay,
  } = props;

  /** バナーの DOM (表示時にスクロールして見える位置に持ってくる) */
  const bannerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = bannerRef.current;
    if (errorMessage && el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [errorMessage]);

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid={`${testIdPrefix}-modal`}
      // 背景クリック / ESC では閉じない (onClick 未設定)
    >
      <form
        className="modal modal--single-column"
        style={{ maxWidth }}
        aria-label={title}
        data-testid={`${testIdPrefix}-form`}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (!saving) onSubmit();
        }}
      >
        <div className="modal-header">
          <h2 style={{ margin: 0, fontSize: 15 }} data-testid={`${testIdPrefix}-form-title`}>
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="閉じる"
            data-testid={`${testIdPrefix}-modal-close`}
          >
            ×
          </button>
        </div>

        <div className="modal-body modal-body--single-column">
          <div className="form-pane">
            {errorMessage ? (
              <div ref={bannerRef}>
                <StatusBanner
                  variant="error"
                  onDismiss={onDismissError}
                  data-testid={`${testIdPrefix}-form-error`}
                >
                  {errorMessage}
                </StatusBanner>
              </div>
            ) : null}
            {children}
          </div>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            data-testid={`${testIdPrefix}-cancel-edit`}
          >
            キャンセル
          </button>
          <button
            type="submit"
            className="primary"
            disabled={saving || saveDisabled}
            data-testid={`${testIdPrefix}-save`}
          >
            {saving ? (
              <>
                <Spinner />
                保存中...
              </>
            ) : (
              saveLabel
            )}
          </button>
        </div>
      </form>

      {overlay}
    </div>
  );
}
